import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Input, Label, Row } from "reactstrap";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import AuthLogo from "../../Components/Auth/AuthLogo";
import AuthCard from "../../Components/Auth/AuthCard";
import AuthContainer from "../../Components/Auth/AuthContainer";
import CustomInput from "../../Components/Custom/CustomInput";
import CustomButton from "../../Components/Custom/CustomButton";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { getFcmToken } from "helpers/firebase/fcm";
import { RegisterArgs, SupportTicketArgs } from "types/auth.api";
import { ErrorHandler, SuccessToast } from "utils/toast";
import EmailVerificationModal from "Components/Modals/EmailVerificationModal";
import { redirectPage } from "utils/redirectPage";
import { loginUser, resendEmailVerification } from "slices/auth/thunk";
import { getConfig } from "slices/thunks";
import { getTourGuideApi, submitSupportTicketApi } from "helpers/ApiManager";
import { hydrateToursFromBackend } from "slices/tour/reducer";
import {
  VALIDATION_MESSAGES,
  VALIDATION_LIMITS,
  createDescriptionValidation,
} from "Components/constants/validation";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import CustomFormModal, { Field } from "Components/Modals/CustomFormModal";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state?.auth);
  const configData = useAppSelector((state) => state.config.configData);
  const [showEmailVerifiedModal, setShowEmailVerifiedModal] =
    useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [isUserNotActive, setIsUserNotActive] = useState<boolean>(false);
  const [supportModal, setSupportModal] = useState<boolean>(false);

  const SupportValidation = useFormik<{
    area: number;
    beSpecific: string;
    desc: string;
  }>({
    enableReinitialize: true,
    initialValues: {
      area: 0,
      beSpecific: "",
      desc: "",
    },
    validationSchema: Yup.object({
      area: Yup.number()
        .min(1, "Area is required")
        .required("Area is required"),

      beSpecific: Yup.string()
        .trim()
        .when("area", (value, schema) => {
          const area = Number(value);
          return area === 6
            ? schema
                .required(VALIDATION_MESSAGES.REQUIRED("this field"))
                .min(
                  VALIDATION_LIMITS.NAME.MIN,
                  VALIDATION_MESSAGES.MIN_LENGTH(
                    "This field",
                    VALIDATION_LIMITS.NAME.MIN,
                  ),
                )
            : schema.notRequired();
        }),

      desc: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("description"))
        .min(
          VALIDATION_LIMITS.DESCRIPTION.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Description",
            VALIDATION_LIMITS.DESCRIPTION.MIN,
          ),
        )
        .max(
          VALIDATION_LIMITS.DESCRIPTION.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Description",
            VALIDATION_LIMITS.DESCRIPTION.MAX,
          ),
        )
        .test(
          "valid-description",
          createDescriptionValidation("Description").message,
          createDescriptionValidation("Description").test,
        ),
    }),
    onSubmit: async (values) => {
      console.log("Support form submitted:", values);
      try {
        let passedData: SupportTicketArgs = {
          support_ticket_area_id: values.area,
          description: values.desc?.trim(),
        };
        if (values.beSpecific) {
          passedData = {
            support_ticket_area_id: values.area,
            other_area: values.beSpecific?.trim(),
            description: values.desc?.trim(),
          };
        }

        let res = await submitSupportTicketApi(passedData);
        if (res.result) {
          SuccessToast("Support ticket submitted successfully");
        }
      } catch (error) {
        ErrorHandler(error);
        console.log("Error submitting support ticket:", error);
      }
      // Close modal after submission
      setSupportModal(false);
      SupportValidation.resetForm();
    },
  });

  let supportFields: Field[] = useMemo(
    () => [
      {
        name: "area",
        label: "Area",
        type: "select",
        placeholder: "Select area",
        options: (configData?.support_ticket_areas || []).map((area) => ({
          label: area.description,
          value: area.id,
        })),
      },
      // Insert only when area === 6
      ...(SupportValidation.values.area === 6
        ? [
            {
              name: "beSpecific",
              label: "Be Specific",
              type: "text",
              placeholder: "Be Specific",
            } as Field,
          ]
        : []),
      {
        name: "desc",
        label: "Description",
        type: "textarea",
        placeholder: "Description",
      },
    ],

    [configData?.support_ticket_areas, SupportValidation.values.area],
  );

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .transform((value) => value?.trim())
        .required(VALIDATION_MESSAGES.REQUIRED("your email"))
        .email(VALIDATION_MESSAGES.INVALID_EMAIL),
      password: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("your password"))
        .test(
          "no-spaces",
          "Password cannot contain spaces",
          (value) => !value || !/\s/.test(value),
        )
        .min(
          VALIDATION_LIMITS.PASSWORD.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Password",
            VALIDATION_LIMITS.PASSWORD.MIN,
          ),
        )
        .max(
          VALIDATION_LIMITS.PASSWORD.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Password",
            VALIDATION_LIMITS.PASSWORD.MAX,
          ),
        ),
    }),
    onSubmit: async (values) => {
      const trimmedEmail = values.email?.trim();
      const trimmedPassword = values.password?.trim();

      if (rememberMe) {
        localStorage.setItem("rememberEmail", trimmedEmail);
        localStorage.setItem("rememberPassword", trimmedPassword);
      } else {
        localStorage.removeItem("rememberEmail");
        localStorage.removeItem("rememberPassword");
      }

      setEmail(trimmedEmail);
      const token = await getFcmToken();
      const loginPayload: RegisterArgs = {
        email: trimmedEmail,
        password: trimmedPassword,
        device_type: "web",
        push_token: token,
      };

      try {
        const res = await dispatch(loginUser(loginPayload))?.unwrap();
        console.log(res);
        if (res?.payload?.role === "admin") {
          navigate("/dashboard", { replace: true });
          SuccessToast(res?.message);
          return;
        }

        if (!res?.payload?.is_email_verified) {
          setShowEmailVerifiedModal(() => !res?.payload?.is_email_verified);
          return;
        }

        if (res.result) {
          await dispatch(getConfig())?.unwrap();

          if (!res?.payload?.is_active) {
            setIsUserNotActive(() => !res?.payload?.is_active);
            return;
          }

          const route = redirectPage(res?.payload, true);
          // console.log(route);

          if (route) {
            navigate(route, { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
          SuccessToast(res?.message);

          const tourRes = await getTourGuideApi();

          console.log("tourRes", tourRes);

          dispatch(
            hydrateToursFromBackend({
              tour_type: tourRes.payload.tour_type,
              user_guide: tourRes.payload.user_guide,
            }),
          );
        } else {
          ErrorHandler(res?.message);
        }
      } catch (error) {
        console.log("error", error);
        ErrorHandler(error);
      }
    },
  });

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberEmail");
    const savedPassword = localStorage.getItem("rememberPassword");

    if (savedEmail && savedPassword) {
      validation.setFieldValue("email", savedEmail);
      validation.setFieldValue("password", savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleCloseVerificationModal = () => {
    setShowEmailVerifiedModal(false);
  };

  const handleResendVerificationEmail = async () => {
    try {
      await dispatch(resendEmailVerification())?.unwrap();
      SuccessToast("Verification email sent successfully");
      setShowEmailVerifiedModal(false);
    } catch (error) {
      ErrorHandler(error);
    }
  };

  const handleRememberMe = () => {
    setRememberMe((prev) => !prev);
  };

  document.title = "SignIn | EaziQuote";
  return (
    <React.Fragment>
      <ParticlesAuth>
        <AuthContainer>
          <Container>
            <Row>
              <Col lg={12}>
                <AuthLogo />
              </Col>
            </Row>

            <Row className="auth-container">
              <Col md={8} lg={6} xl={5}>
                <AuthCard>
                  <div className="text-center mt-2">
                    <h5 className="text-black2D font-primary fw-semibold fs-24">
                      Welcome Back 👋
                    </h5>
                    <p className="font-primary fs-14 text-grey89 fw-normal">
                      {" "}
                      Log in to manage your quotes and invoices with ease.
                    </p>
                  </div>
                  <div className="p-2 mt-4">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                      }}
                    >
                      <CustomInput
                        label="Email"
                        placeholder="Enter your email"
                        name="email"
                        type="email"
                        value={validation.values.email}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.email}
                        error={validation.errors.email}
                      />
                      <CustomInput
                        label="Password"
                        placeholder="Enter your password"
                        name="password"
                        type="password"
                        value={validation.values.password}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.password}
                        error={validation.errors.password}
                        isPassword={true}
                      />

                      <div className="form-check d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            checked={rememberMe}
                            onChange={handleRememberMe}
                            id="auth-remember-check"
                            style={{ width: 20, height: 20, marginRight: 5 }}
                          />
                          <Label
                            className="form-check-label text-black1C fs-16 font-primary fw-normal"
                            htmlFor="auth-remember-check"
                          >
                            Remember me
                          </Label>
                        </div>
                        <div className="float-end">
                          <Link
                            to="/forgot-password"
                            className="text-primary font-primary fs-16 fw-normal"
                          >
                            Forgot Password?
                          </Link>
                        </div>
                      </div>

                      <CustomButton
                        text="Sign In"
                        type="submit"
                        loading={isLoading}
                        className="auth-Button z-3"
                      />

                      <div className="mt-4 text-center">
                        <p className="mb-0 font-primary fs-16 fw-normal text-grey89">
                          Don't have an account ?{" "}
                          <Link
                            to="/register"
                            className="text-primary font-primary fs-16 fw-normal"
                          >
                            Create Account
                          </Link>
                        </p>
                      </div>
                    </form>
                  </div>
                </AuthCard>
              </Col>
            </Row>
            <EmailVerificationModal
              isOpen={showEmailVerifiedModal}
              email={email}
              onClose={handleCloseVerificationModal}
              onResendEmail={handleResendVerificationEmail}
              isLoading={isLoading}
            />
            <RestrictedAccessModal
              isOpen={isUserNotActive}
              onCancel={() => setIsUserNotActive(false)}
              iconName="AccessDenied"
              title="Access Denied"
              description="Access to EaziQuote has been restricted by the admin. Please contact our support team to resolve this issue."
              buttonText="Contact Support"
              onButtonClick={() => {
                setIsUserNotActive(false);
                setSupportModal(true);
              }}
            />

            <CustomFormModal
              isOpen={supportModal}
              onCancel={() => {
                setSupportModal(false);
              }}
              fields={supportFields}
              title={"Support"}
              submitText="Submit"
              validation={SupportValidation}
              isLoading={SupportValidation.isSubmitting}
            />
          </Container>
        </AuthContainer>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default Login;
